<template>
  <div class="article-section-page">
    <inner-hero
      :banners="banners"
      :background-image="FALLBACK_HERO_IMAGE"
      :title="heroTitle"
      :show-breadcrumb="false"
    />
    <article-browser :category="categoryCode" :page-size="pageSize" />
    <contact-action />
  </div>
</template>

<script setup lang="ts">
  import type { BannerItem } from '~/types/site-api'

  import { findArticleCategory, getArticleCategoryRoute } from '~/utils/content'
  import { createBreadcrumbJsonLd, createSiteUrl, getSiteUrl, siteSeo } from '~/utils/seo'

  interface Props {
    pageSize?: number
  }

  defineOptions({
    name: 'ArticleSectionPage',
  })

  withDefaults(defineProps<Props>(), {
    pageSize: 9,
  })

  const route = useRoute()
  const categoryCode = computed(() => {
    const value = route.params.category
    return String(Array.isArray(value) ? (value[0] ?? '') : (value ?? ''))
  })
  const { data: categoryResponse } = useArticleCategories()
  const categoryTree = computed(() => categoryResponse.value?.data ?? [])
  const categoryMatch = computed(() => findArticleCategory(categoryTree.value, categoryCode.value))
  const category = computed(() => categoryMatch.value?.category)
  const rootCategory = computed(() => categoryMatch.value?.root)
  const categoryRoute = computed(() =>
    category.value ? getArticleCategoryRoute(category.value) : undefined
  )
  const pageTitle = computed(() => categoryRoute.value?.label ?? '')
  const pageDescription = computed(() =>
    rootCategory.value && rootCategory.value !== category.value
      ? rootCategory.value.name
      : undefined
  )
  const heroTitle = computed(() => rootCategory.value?.name ?? pageTitle.value)
  const seoTitle = computed(() => `${pageTitle.value} | ${siteSeo.shortName}`)
  const siteUrl = getSiteUrl()

  const { getBanners } = useSiteApi()
  const bannerPosition = computed(() => rootCategory.value?.id ?? '')

  /** banner 接口无数据或请求失败时的兜底背景图。 */
  const FALLBACK_HERO_IMAGE = '/images/article/article-hero.webp'

  const { data: bannerData } = await useAsyncData(
    'article-section-banner',
    async (_nuxtApp, { signal }) => {
      const id = String(bannerPosition.value)
      if (!id) return { data: [] as BannerItem[] }
      try {
        return await getBanners({ position: id }, signal)
      } catch (error) {
        if (signal.aborted) throw error
        return { data: [] as BannerItem[] }
      }
    },
    { watch: [bannerPosition], default: () => ({ data: [] as BannerItem[] }) }
  )

  const banners = computed(() => bannerData.value?.data ?? [])

  if (!categoryCode.value || !categoryRoute.value) {
    throw createError({
      statusCode: 404,
      statusMessage: '资讯栏目不存在',
    })
  }

  useSeoMeta({
    title: seoTitle,
    description: pageDescription,
    ogTitle: seoTitle,
    ogDescription: pageDescription,
    ogType: 'website',
    ogImage: siteSeo.ogImage,
    twitterTitle: seoTitle,
    twitterDescription: pageDescription,
    twitterImage: siteSeo.ogImage,
  })

  useHead(() => ({
    link: [
      { rel: 'canonical', href: createSiteUrl(categoryRoute.value?.path || route.path, siteUrl) },
    ],
  }))
  useJsonLd(() =>
    createBreadcrumbJsonLd(
      [
        { name: '首页', path: '/' },
        {
          name: pageTitle.value || '资讯',
          path: categoryRoute.value?.path || route.path,
        },
      ],
      siteUrl
    )
  )
</script>

<style scoped lang="scss">
  @media (min-width: 768px) {
    :deep(.inner-hero) {
      min-height: 220px;
    }
  }

  @media (max-width: 767px) {
    :deep(.inner-hero) {
      min-height: 180px;
    }
  }

  :deep(.inner-hero h1) {
    font-size: clamp(28px, 3vw, 40px);
  }

  /* 移除文章页 hero 的渐变遮罩 */
  :deep(.inner-hero)::after {
    content: none;
  }

  /* 文章列表 hero 收窄至 1280px，与列表区对齐 */
  :deep(.inner-hero-content) {
    @media (min-width: 768px) {
      padding-inline: 320px;
    }

    @media (min-width: 1440px) {
      padding-inline: calc(50vw - 640px);
    }
  }
</style>
